import { computed, inject } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { environment } from 'src/environments/environment';

import {
  EntitySettingTypes,
  EntityType,
  GqlService,
  TrialAttributes,
} from '@shared/services/gql.service';
import { MainQuery } from '@shared/store/main/main.query';
import { gqlToPromise } from '@shared/utils/gql-to-promise';

export type PivotSettings = {
  attributes: TrialAttributes[];
  primary: {
    attribute_name: string;
    expense_populated: boolean;
    values_populated: boolean;
  };
  secondary: {
    attribute_name: string;
    expense_populated: boolean;
    values_populated: boolean;
  };
};

export type TrialPivotSettings = {
  primary: string;
  secondary: string;
};

export const injectTrialPivotSettings = () => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);

  return injectQuery(() => ({
    queryFn: async () => {
      if (environment.usePlayground) {
        return { primary: 'CLASS', secondary: 'ACCOUNT' };
      }

      const data = await gqlToPromise(() =>
        gqlService.listEntitySettings$({
          entity_ids: [mainQuery.trialKey()],
          entity_type: EntityType.TRIAL,
          setting_type: EntitySettingTypes.CLOSE_HIERARCHY,
        })
      );

      try {
        const settings = JSON.parse(
          data?.[0]?.settings || '{"ATTRIBUTES":{"PRIMARY":"","SECONDARY":""}}'
        );

        return <TrialPivotSettings>{
          primary: settings?.ATTRIBUTES?.PRIMARY as string,
          secondary: settings?.ATTRIBUTES?.SECONDARY as string,
        };
      } catch (error) {
        console.error(error);

        return <TrialPivotSettings>{
          primary: '',
          secondary: '',
        };
      }
    },
    queryKey: ['trials', 'detail', mainQuery.trialKey(), 'pivot-settings'],
  }));
};

export const injectTrialAttributes = () => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);

  return injectQuery(() => ({
    queryFn: () => {
      if (environment.usePlayground) {
        return import('./trial-attributes.mock').then((m) => m.trialAttributesMock.data);
      }

      return gqlToPromise(() => gqlService.listTrialAttributes$(true)).then((data) =>
        data?.sort((a, b) => a.attribute_index - b.attribute_index)
      );
    },
    queryKey: ['trials', 'detail', mainQuery.trialKey(), 'attributes'],
  }));
};

export const injectPivotSettings = () => {
  const trialPivotSettings = injectTrialPivotSettings();
  const trialAttributes = injectTrialAttributes();

  return {
    data: computed(() => {
      const pivotSettings = trialPivotSettings.data();
      const attributes = trialAttributes.data();

      if (!pivotSettings || !attributes) {
        return <PivotSettings>{
          attributes: [],
          primary: {
            attribute_name: '',
            expense_populated: false,
            values_populated: false,
          },
          secondary: {
            attribute_name: '',
            expense_populated: false,
            values_populated: false,
          },
        };
      }

      const primaryAttribute = attributes.find(
        (attribute) => attribute.attribute_name === pivotSettings.primary
      );
      const secondaryAttribute = attributes.find(
        (attribute) => attribute.attribute_name === pivotSettings.secondary
      );

      return <PivotSettings>{
        attributes,
        primary: {
          attribute_name: pivotSettings.primary,
          expense_populated: primaryAttribute?.attribute_expense_populated || false,
          values_populated: primaryAttribute?.attribute_values_populated || false,
        },
        secondary: {
          attribute_name: pivotSettings.secondary,
          expense_populated: secondaryAttribute?.attribute_expense_populated || false,
          values_populated: secondaryAttribute?.attribute_values_populated || false,
        },
      };
    }),
    loading: computed(() => trialPivotSettings.isLoading() || trialAttributes.isLoading()),
    refreshAttributes: () => {
      void trialPivotSettings.refetch();
      void trialAttributes.refetch();
    },
  };
};

export const injectUpdatePivotSettings = () => {
  const mainQuery = inject(MainQuery);
  const gqlService = inject(GqlService);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (params: {
      bucketKeys: string[];
      notes: string;
      pivotSettings: { primary: string; secondary: string };
    }) =>
      gqlToPromise(() =>
        gqlService.updateEntitySettings$({
          entity_ids: [mainQuery.trialKey()],
          entity_type: EntityType.TRIAL,
          setting_type: EntitySettingTypes.CLOSE_HIERARCHY,
          settings: JSON.stringify({
            ATTRIBUTES: {
              PRIMARY: params.pivotSettings.primary,
              SECONDARY: params.pivotSettings.secondary,
            },
            BUCKET_KEYS: params.bucketKeys,
            NOTES: params.notes,
          }),
        })
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['trials', 'detail', mainQuery.trialKey(), 'pivot-settings'],
      });
    },
  }));
};
